import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Star, StarOff, ArchiveRestore, Pencil, Eye, Plus, Archive, CheckCircle2, Library, FileText,
} from "lucide-react";
import { AdminLayout } from "./AdminLayout";
import {
  adminListKnowledge,
  adminPublishKnowledge,
  adminArchiveKnowledge,
  adminToggleFeaturedKnowledge,
} from "@/services/knowledgeService";
import { KNOWLEDGE_CATEGORIES, CATEGORY_LABEL } from "@/constants/knowledgeCategories";

/**
 * Admin · Knowledge Library — list + status controls.
 *
 * MVP scope: table view of every story (any status), quick publish /
 * archive / toggle-featured actions, and links to the editor for
 * create + edit. Preview opens the public URL.
 */

const STATUS_TONE = {
  published: "bg-emerald-50 text-emerald-700 border-emerald-200",
  draft:     "bg-surface-100 text-ink-700 border-surface-200",
  archived:  "bg-risk-50 text-risk-700 border-risk-100",
};
const STATUS_LABEL = {
  published: "منشورة",
  draft:     "مسوَّدة",
  archived:  "مؤرشفة",
};
const CAT_LABEL = CATEGORY_LABEL;

export default function AdminKnowledgeListPage() {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab]         = useState("all");         // all | published | draft | archived
  const [category, setCategory] = useState("all");       // all | person | company
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminListKnowledge();
      setRows(data || []);
    } catch (e) {
      toast.error("تعذّر تحميل مكتبة المعرفة", { description: String(e.message || e) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let out = rows;
    if (tab !== "all")      out = out.filter((r) => r.status === tab);
    if (category !== "all") out = out.filter((r) => r.category === category);
    return out;
  }, [rows, tab, category]);

  const kpi = useMemo(() => ({
    total:     rows.length,
    published: rows.filter((r) => r.status === "published").length,
    draft:     rows.filter((r) => r.status === "draft").length,
    archived:  rows.filter((r) => r.status === "archived").length,
  }), [rows]);

  const onPublish = async (row) => {
    try {
      await adminPublishKnowledge(row.id);
      toast.success(`نُشرت قصّة ${row.subjectName}`);
      await load();
    } catch (e) { toast.error("تعذّر النشر", { description: String(e.message || e) }); }
  };
  const onArchive = async (row) => {
    try {
      await adminArchiveKnowledge(row.id);
      toast.success(`أُرشفت قصّة ${row.subjectName}`);
      await load();
    } catch (e) { toast.error("تعذّر الأرشفة", { description: String(e.message || e) }); }
  };
  const onToggleFeatured = async (row) => {
    try {
      await adminToggleFeaturedKnowledge(row.id);
      toast.success(row.isFeatured ? "أُلغي التميّز" : "أصبحت مميّزة");
      await load();
    } catch (e) { toast.error("تعذّر التحديث", { description: String(e.message || e) }); }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-surface-200 bg-surface-50 px-3 py-1 text-[10.5px] font-semibold uppercase tracking-widest text-intel-800">
              <Library className="h-3 w-3 text-intel-700" strokeWidth={2} aria-hidden />
              Executive Learning Library
            </div>
            <h1 className="mt-2 text-2xl font-semibold tracking-tight text-ink-900">مكتبة المعرفة</h1>
            <p className="mt-1 text-sm text-ink-500">أدر قصص النجاح — شخصيات وشركات — ونشر وأرشفة وتحرير.</p>
          </div>
          <button
            data-testid="admin-knowledge-new"
            onClick={() => navigate("/admin/knowledge/new")}
            className="inline-flex h-10 items-center gap-1.5 rounded-lg bg-fursa-700 px-4 text-sm font-medium text-white transition-colors hover:bg-fursa-800"
          >
            <Plus className="h-4 w-4" strokeWidth={2} aria-hidden />
            قصّة جديدة
          </button>
        </header>

        {/* KPIs */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Kpi label="الإجمالي"  value={kpi.total}     />
          <Kpi label="منشورة"    value={kpi.published} tone="emerald" />
          <Kpi label="مسوَّدة"    value={kpi.draft}     tone="ink" />
          <Kpi label="مؤرشفة"    value={kpi.archived}  tone="risk" />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-surface-200 bg-white p-2">
          <FilterTab active={tab === "all"}       onClick={() => setTab("all")}       label="الكل"     count={rows.length} testId="knowledge-tab-all" />
          <FilterTab active={tab === "published"} onClick={() => setTab("published")} label="منشورة"    count={kpi.published} testId="knowledge-tab-published" />
          <FilterTab active={tab === "draft"}     onClick={() => setTab("draft")}     label="مسوَّدة"    count={kpi.draft} testId="knowledge-tab-draft" />
          <FilterTab active={tab === "archived"}  onClick={() => setTab("archived")}  label="مؤرشفة"    count={kpi.archived} testId="knowledge-tab-archived" />
          <span aria-hidden className="mx-2 h-5 w-px bg-surface-200" />
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            data-testid="knowledge-category-filter"
            className="h-9 rounded-md border border-surface-200 bg-white px-3 text-sm text-ink-700"
          >
            <option value="all">جميع التصنيفات</option>
            {KNOWLEDGE_CATEGORIES.map((c) => (
              <option key={c.key} value={c.key} disabled={!c.available}>
                {c.label}{c.available ? "" : " (قريباً)"}
              </option>
            ))}
          </select>
          <span className="ms-auto text-[11px] text-ink-500">
            <span dir="ltr" className="num font-mono tabular-nums">{filtered.length}</span> عنصر
          </span>
        </div>

        {/* Table */}
        <div className="overflow-hidden rounded-xl border border-surface-200 bg-white shadow-card">
          {loading && <div className="p-8 text-center text-sm text-ink-500">جارٍ التحميل…</div>}
          {!loading && filtered.length === 0 && (
            <div data-testid="knowledge-empty" className="flex flex-col items-center justify-center gap-3 p-12 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-surface-50 ring-1 ring-surface-200">
                <FileText className="h-5 w-5 text-ink-500" strokeWidth={1.5} aria-hidden />
              </div>
              <p className="text-sm font-medium text-ink-900">
                {tab === "draft"     && "لا توجد مسوّدات بعد"}
                {tab === "archived"  && "لا توجد قصص مؤرشفة"}
                {tab === "published" && "لا توجد قصص منشورة"}
                {tab === "all"       && "لا توجد نتائج لهذه الفلاتر"}
              </p>
              <p className="max-w-xs text-[12.5px] text-ink-500">
                {tab === "draft"     && "ابدأ بإنشاء قصّة جديدة كمسوّدة، ثم راجعها قبل النشر."}
                {tab === "archived"  && "القصص المؤرشفة تظهر هنا وتخرج من واجهة المكتبة العامة."}
                {tab === "published" && "لم تُنشر أي قصّة بعد. أنشئ قصّة وانشرها لتظهر في مكتبة المعرفة."}
                {tab === "all"       && "جرّب تصنيفاً مختلفاً أو أنشئ قصّة جديدة."}
              </p>
              {(tab === "draft" || tab === "published" || tab === "all") && (
                <button
                  type="button"
                  onClick={() => navigate("/admin/knowledge/new")}
                  data-testid="knowledge-empty-cta"
                  className="mt-2 inline-flex h-9 items-center gap-1.5 rounded-lg bg-fursa-700 px-4 text-[13px] font-medium text-white hover:bg-fursa-800"
                >
                  <Plus className="h-3.5 w-3.5" strokeWidth={2} aria-hidden />
                  قصّة جديدة
                </button>
              )}
            </div>
          )}
          {!loading && filtered.length > 0 && (
            <table className="min-w-full text-right text-sm">
              <thead className="border-b border-surface-200 bg-surface-50/60 text-[11px] font-semibold uppercase tracking-wider text-ink-500">
                <tr>
                  <th className="px-4 py-3">القصّة</th>
                  <th className="px-4 py-3">التصنيف</th>
                  <th className="px-4 py-3">الحالة</th>
                  <th className="px-4 py-3">التاريخ</th>
                  <th className="px-4 py-3 text-center">إجراءات</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr
                    key={r.id}
                    data-testid={`admin-knowledge-row-${r.slug}`}
                    className="border-b border-surface-100 last:border-0"
                  >
                    <td className="max-w-xl px-4 py-3">
                      <div className="flex items-center gap-2">
                        {r.isFeatured && <Star className="h-3.5 w-3.5 text-gold-600" strokeWidth={2} aria-hidden />}
                        <span className="font-medium text-ink-900 line-clamp-1">{r.title}</span>
                      </div>
                      <div className="mt-0.5 text-[11.5px] text-ink-500">
                        {r.subjectName} · <span dir="ltr" className="num">{r.readTimeMinutes}</span> د
                      </div>
                    </td>
                    <td className="px-4 py-3 text-ink-700">{CAT_LABEL[r.category]}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10.5px] font-medium ${STATUS_TONE[r.status] || ""}`}>
                        {STATUS_LABEL[r.status] || r.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-[11.5px] text-ink-500">{r.publishedDisplay || "—"}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1">
                        <IconAction
                          testId={`admin-knowledge-preview-${r.slug}`}
                          label="معاينة"
                          Icon={Eye}
                          onClick={() => window.open(`/knowledge/${r.slug}`, "_blank", "noopener")}
                        />
                        <IconAction
                          testId={`admin-knowledge-edit-${r.slug}`}
                          label="تحرير"
                          Icon={Pencil}
                          onClick={() => navigate(`/admin/knowledge/${r.slug}`)}
                        />
                        <IconAction
                          testId={`admin-knowledge-featured-${r.slug}`}
                          label={r.isFeatured ? "إلغاء التميّز" : "جعلها مميّزة"}
                          Icon={r.isFeatured ? StarOff : Star}
                          tone={r.isFeatured ? "gold" : undefined}
                          onClick={() => onToggleFeatured(r)}
                        />
                        {r.status !== "published" && (
                          <IconAction
                            testId={`admin-knowledge-publish-${r.slug}`}
                            label="نشر"
                            Icon={CheckCircle2}
                            tone="emerald"
                            onClick={() => onPublish(r)}
                          />
                        )}
                        {r.status !== "archived" ? (
                          <IconAction
                            testId={`admin-knowledge-archive-${r.slug}`}
                            label="أرشفة"
                            Icon={Archive}
                            tone="risk"
                            onClick={() => onArchive(r)}
                          />
                        ) : (
                          <IconAction
                            testId={`admin-knowledge-restore-${r.slug}`}
                            label="استعادة"
                            Icon={ArchiveRestore}
                            onClick={() => onPublish(r)}
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}

/* ─── Primitives ─────────────────────────────────────────────── */

const Kpi = ({ label, value, tone }) => {
  const toneCls = tone === "emerald" ? "text-emerald-700"
              : tone === "ink" ? "text-ink-700"
              : tone === "risk" ? "text-risk-700"
              : "text-ink-900";
  return (
    <div className="rounded-xl border border-surface-200 bg-white p-4 shadow-card">
      <p className="text-[10.5px] font-semibold uppercase tracking-widest text-ink-500">{label}</p>
      <p dir="ltr" className={`num mt-1 font-mono text-[24px] font-bold tabular-nums ${toneCls}`}>
        {value}
      </p>
    </div>
  );
};

const FilterTab = ({ active, onClick, label, count, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[12.5px] font-medium transition-colors ${
      active ? "bg-intel-900 text-white" : "text-ink-700 hover:bg-surface-100"
    }`}
    aria-pressed={active}
  >
    {label}
    <span
      dir="ltr"
      className={`num font-mono text-[10.5px] tabular-nums ${
        active ? "text-white/80" : "text-ink-500"
      }`}
    >
      {count}
    </span>
  </button>
);

const IconAction = ({ Icon, label, onClick, testId, tone }) => {
  const toneCls = tone === "emerald" ? "hover:text-emerald-700"
             : tone === "risk"    ? "hover:text-risk-700"
             : tone === "gold"    ? "text-gold-600 hover:text-gold-700"
             : "hover:text-intel-900";
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      data-testid={testId}
      className={`inline-flex h-8 w-8 items-center justify-center rounded-md text-ink-500 transition-colors ${toneCls}`}
    >
      <Icon className="h-4 w-4" strokeWidth={1.75} aria-hidden />
    </button>
  );
};
